import { motion } from "framer-motion";
import PageHeader from "@/components/PageHeader";
import CTASection from "@/components/CTASection";
import SectionHeading from "@/components/SectionHeading";
import { useLanguage } from "@/hooks/useLanguage";
import {
  CNC_PRODUCT_FAMILIES,
  PRESS_BRAKE_FAMILIES,
  TUBE_FABRICATION_FAMILIES,
  STANDARD_PARTS_LIBRARY,
} from "@/data/products";

function FamilyCard({ family, index }: { family: { title: string; description: string; variations: string[] }; index: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.5, delay: index * 0.08, ease: [0.25, 0.46, 0.45, 0.94] }}
      className="bg-white rounded-lg p-6 border border-muted-border transition-all duration-300 hover:shadow-card-hover"
    >
      <div className="h-[3px] w-10 bg-gold rounded-full mb-4" />
      <h3 className="font-display font-bold text-lg text-graphite mb-2">{family.title}</h3>
      <p className="text-sm text-steel mb-4 leading-relaxed">{family.description}</p>
      <ul className="space-y-1.5">
        {family.variations.map((v) => (
          <li key={v} className="flex items-center gap-2">
            <span className="w-1 h-1 rounded-full bg-steel shrink-0" />
            <span className="text-sm text-steel">{v}</span>
          </li>
        ))}
      </ul>
    </motion.div>
  );
}

export default function CNC() {
  const { t } = useLanguage();
  t("cncTitle"); // ensure translation context is loaded

  return (
    <>
      <PageHeader titleKey="cncTitle" subtitleKey="cncSubtitle" />

      <section className="bg-white section-padding px-4 sm:px-6 lg:px-8">
        <div className="content-container">
          <SectionHeading overline="CNC LASER CUTTING" titleKey="cncLaserCutComponents" descriptionKey="cncLaserCutDesc" align="left" />
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {CNC_PRODUCT_FAMILIES.map((f, i) => (
              <FamilyCard key={f.title} family={f} index={i} />
            ))}
          </div>
        </div>
      </section>

      <section className="bg-steel-light section-padding px-4 sm:px-6 lg:px-8">
        <div className="content-container">
          <SectionHeading overline="PRESS BRAKE BENDING" titleKey="pressBrakeFormed" descriptionKey="pressBrakeFormedDesc" align="left" />
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
            {PRESS_BRAKE_FAMILIES.map((f, i) => (
              <FamilyCard key={f.title} family={f} index={i} />
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white section-padding px-4 sm:px-6 lg:px-8">
        <div className="content-container">
          <SectionHeading overline="TUBE & PIPE FABRICATION" titleKey="tubeFabSection" descriptionKey="tubeFabSectionDesc" align="left" />
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {TUBE_FABRICATION_FAMILIES.map((f, i) => (
              <FamilyCard key={f.title} family={f} index={i} />
            ))}
          </div>
        </div>
      </section>

      <section className="bg-steel-light section-padding px-4 sm:px-6 lg:px-8">
        <div className="content-container">
          <SectionHeading overline="STANDARD PARTS" titleKey="standardParts" descriptionKey="standardPartsDesc" align="center" />
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {STANDARD_PARTS_LIBRARY.map((family, i) => (
              <motion.div
                key={family.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.5, delay: i * 0.08, ease: [0.25, 0.46, 0.45, 0.94] }}
                className="bg-white rounded-lg p-6 border border-muted-border transition-all duration-300 hover:shadow-card-hover"
              >
                <div className="h-[3px] w-10 bg-gold rounded-full mb-4" />
                <h3 className="font-display font-bold text-lg text-graphite mb-3">{family.title}</h3>
                <ul className="space-y-1.5">
                  {family.items.map((item) => (
                    <li key={item} className="flex items-center gap-2">
                      <span className="w-1 h-1 rounded-full bg-steel shrink-0" />
                      <span className="text-sm text-steel">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <CTASection titleKey="customPart" descKey="customPartDesc" buttons={[{ labelKey: "sendCustomInquiry", href: "/contact", type: "white" }]} bgColor="dark" />
    </>
  );
}
